import { defineStore, storeToRefs } from 'pinia'

import api from '@/api/api'
import API from '@/api/apiUrls'
import { ref } from 'vue'
import { useSpeckleAuthStore, useSpeckleStore } from '@haskoning/vue-speckle'
import type { SpeckleModelMutation } from '@haskoning/js-speckle'
import type { ProjectSpeckleType } from '@/store/type/projectSpeckle.type'

export const MODEL = 'pace-geoprofile connection'

export const useProjectSpeckleStore = defineStore('projectSpeckleStore', () => {
  const currentItem = ref<ProjectSpeckleType | null>(null)
  const writeLoading = ref<boolean>(false)

  const DEFAULT_MESSAGE = 'Exported from PaCE'
  const message = ref<string>(DEFAULT_MESSAGE)

  const importSectionLineFromSpeckleDialog = ref<boolean>(false)
  const exportToSpeckleDialog = ref<boolean>(false)
  const connectToSpeckleDialog = ref<boolean>(false)

  const resetRelatedSpeckleData = () => {
    const speckleStore = useSpeckleStore()
    speckleStore.currentSelection.project = undefined
    speckleStore.currentSelection.model = undefined
    speckleStore.currentSelection.version = undefined
    speckleStore.allProjects = []
    speckleStore.allModels = []
    speckleStore.allVersions = []
    currentItem.value = null
    message.value = DEFAULT_MESSAGE
  }

  const logout = () => {
    const speckleAuth = useSpeckleAuthStore()
    speckleAuth.disconnect()
    resetRelatedSpeckleData()
    localStorage.removeItem('speckleAccessToken')
    localStorage.removeItem('speckleRefreshToken')
    localStorage.removeItem('speckleRedirectUrl')
    localStorage.removeItem('speckleAppId')
    localStorage.removeItem('speckleAppSecret')
    localStorage.removeItem('speckleAppName')
  }

  const fetchProjectSpeckle = async (projectId: number) => {
    const url = API.SPECKLE.PROJECT_SPECKLE.FETCH_PROJECT_SPECKLE(projectId)
    const { data } = await api.get(url)
    if (data.length) {
      currentItem.value = data[0] as ProjectSpeckleType
      const projectSpeckleId = currentItem.value?.project_id
      const speckleStore = useSpeckleStore()
      const { currentSelection, allProjects } = storeToRefs(speckleStore)
      const project = allProjects.value.find((proj) => proj.id === projectSpeckleId)
      if (currentSelection.value && project) {
        currentSelection.value.project = project
      }
    }
  }

  const addDefaultModel = async (projectId: string) => {
    const speckleStore = useSpeckleStore()
    const { allModels } = storeToRefs(speckleStore)

    const index = allModels.value.findIndex((model) => model.name === MODEL)
    if (index === -1) {
      const defaultModel: SpeckleModelMutation = {
        name: MODEL,
        description: '',
        projectId: projectId
      }
      await speckleStore.createSpeckleProjectModel(defaultModel)
    }
  }

  const updateProjectSpeckle = async () => {
    const speckleStore = useSpeckleStore()
    if (currentItem.value && speckleStore.currentSelection.project) {
      currentItem.value.project_id = speckleStore.currentSelection.project?.id
      const url = API.SPECKLE.PROJECT_SPECKLE.INDEX + `${currentItem.value.id}/`
      try {
        writeLoading.value = true
        const { data } = await api.patch(url, currentItem.value)
        currentItem.value = data
        writeLoading.value = false
      } catch (error) {
        console.error('Failed to update project speckle:', error)
      }
    }
  }

  const send = async (projectId: number, message: string) => {
    const speckleAuthStore = useSpeckleAuthStore()
    const speckleStore = useSpeckleStore()
    const model_name = speckleStore.currentSelection.model?.name
    const payload = {
      message,
      token: speckleAuthStore.token,
      model_name
    }
    const url = API.SPECKLE.COMMUNICATION_SPECKLE.SEND(projectId)
    writeLoading.value = true
    await api.post(url, payload)
    writeLoading.value = false
  }

  return {
    currentItem,
    writeLoading,
    message,
    importSectionLineFromSpeckleDialog,
    exportToSpeckleDialog,
    connectToSpeckleDialog,
    send,
    updateProjectSpeckle,
    fetchProjectSpeckle,
    addDefaultModel,
    resetRelatedSpeckleData,
    logout
  }
})
